import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import { resetSafetyMeetingStore } from '../../../redux';
import {  usePagination } from '../../../hooks';
import { ASSIGNED_SAFETY_MEETINGS_CUSTOMER_COLUMN, QUERY_KEYS, ROUTES } from '../../../constants';
import { AdminPageHeader } from '../../Admin';
import { Table } from '../../shared';
import { getSafetyServiceAssociationByAccountIdService } from '../../../utils/apiServices/safetyServiceAPI';
import { DownloadIcon } from '../../../assets/svg';
import useBulkDownloadFiles from '../../../hooks/useBulkDownloadFiles';
import YearMonthPopoverPicker from '../../shared/common/YearMonthPopoverPicker';
import useWindowSize from '../../../hooks/useWindowSize';
import { useDebouncedValue } from '../../../hooks/useDebounceValue';
import { popup } from '../../../utils';

const INITIAL_RESPONSE = {
    data: [],
    count: 1,
};

const SafetyMeetingsCustomer = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [response, setResponse] = useState(INITIAL_RESPONSE);
    const { batchDownloadFiles } = useBulkDownloadFiles();
    const { width } = useWindowSize();

    const selectedSafetyMeetings = useSelector((s) => s.safetyMeetings.selectedSafetyMeetings);
    const maxHeight = selectedSafetyMeetings.length > 1 ? 'calc(100dvh - 420px)' : '100%';
    const minHeight = width > 771 ? '48px' : '38px';
    const isUserPrimary = useSelector((s) => s.accountArray.isUserPrimary);
    const accountId = useSelector((s) => s.accountArray.selectedAccount);
    const accountName = useSelector((s) => s.accountArray.selectedAccountName);
    const pagination = usePagination(response.data.length, response.count, 50);
    const [search, setSearch] = useState('');
    const [dateFilter, setDateFilter] = useState({ month: '', year: '' });

    const [debounceSearch] = useDebouncedValue(search, 500);
    const { data, isFetching, isFetched, isError, error } = useQuery({
        queryKey: [
            QUERY_KEYS.SAFETY_MEETING_ASSOCIATION_BY_ID,
            accountId,
            debounceSearch,
            pagination.limit,
            pagination.offset,
            dateFilter.year,
            dateFilter.month,
        ],
        queryFn: async () =>
            await getSafetyServiceAssociationByAccountIdService({
                accountId: accountId,
                search: debounceSearch,
                offset: pagination.offset,
                limit: pagination.limit,
                year: dateFilter.year,
                month: dateFilter.month,
            }),
        select: (data) => ({ data: data.data, count: data.count }),
        placeholderData: { data: [], count: 0 },
        enabled: !!accountId,
    });

    useEffect(() => {
        if (data.data && isFetched) {
            setResponse({ data: data.data, count: data.count });
        }
    }, [data, isFetched]);

    if (isError) popup('error', error.message);

    useEffect(() => {
        if (!isUserPrimary) {
            navigate(ROUTES.CUSTOMER.DASHBOARD);
        }

        return () => {
            setResponse(INITIAL_RESPONSE);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isUserPrimary, accountId]);

    const bulkDownloadHandler = () => {
        const data = selectedSafetyMeetings?.map((safetyMeeting) => ({
            s3_key: safetyMeeting.s3_key,
            logoUrl: '',
            customerName: accountName,
            fileName: safetyMeeting.file_name,
        }));
        batchDownloadFiles(data);
        dispatch(resetSafetyMeetingStore());
    };

    return (
        <>
            <AdminPageHeader
                heading={`${accountName}'s Safety Meetings`}
                backbutton
                showSearchBar
                searchBarPlaceholder={'Search Safety Meetings...'}
                count={pagination.dataCount}
                setSearch={setSearch}
                searchAdjacentChildren={
                    <div className={width < 771 ? 'd-flex w-100 justify-content-end' : ''}>
                        <YearMonthPopoverPicker
                            onChange={(v) => {
                                if (v) {
                                    const [month, year] = v.split('/');
                                    setDateFilter({ month, year });
                                } else {
                                    setDateFilter({ month: '', year: '' });
                                }
                            }}
                            selectStyle={{ width: '120px', minHeight: minHeight }}
                            label={'Assigned Month/Year:'}
                            title={'Filter Meetings By Month/Year'}
                        />
                    </div>
                }
            />

            <Table
                columns={ASSIGNED_SAFETY_MEETINGS_CUSTOMER_COLUMN}
                data={response.data}
                pagination={pagination}
                pageName='safetyMeeting(s)'
                tableStyle={{ maxHeight: maxHeight }}
                stickyColumns={['File Name', 0]}
                isLoading={isFetching}
            />
            {selectedSafetyMeetings.length > 1 && (
                <>
                    <div className='divider mb-3'></div>
                    <div className='d-flex justify-content-center w-100' style={{ height: '56px' }}>
                        <button
                            className='ssc-primary-green-btn'
                            type='button'
                            onClick={bulkDownloadHandler}
                            style={{
                                width: 200,
                                height: 40,
                                fontSize: 16,
                                color: 'white',
                            }}
                            title='Download all as ZIP'
                        >
                            <span className='d-flex align-items-center gap-2'>
                                <span>Download all</span>
                                <DownloadIcon />
                            </span>
                        </button>
                    </div>
                </>
            )}
        </>
    );
};

export default SafetyMeetingsCustomer;
